import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { observer } from "mobx-react-lite";
import { Box, Text, Flex, Icon, IconButton, Tooltip } from "@chakra-ui/react";
import { GripHorizontal, Pencil, Trash2, ChevronRight } from "lucide-react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ILocationMenuInsight } from "../../../stores/data/operationRuleLocations/OperationRuleLocationModel";
import { EventListProps } from "./types";

interface SortableRowProps {
  event: ILocationMenuInsight;
  canDrag: boolean;
  disableDragAndDrop: boolean;
  t: (key: string) => string;
  tInsight: (key: string) => string;
  locationId: number;
  locationName: string;
  onDeleteEvent?: (
    eventId: number,
    eventName: string,
    locationId: number
  ) => void;
  onEditEvent?: (
    event: ILocationMenuInsight,
    locationId: number,
    locationName: string
  ) => void;
}

const SortableRow: React.FC<SortableRowProps> = observer(
  ({
    event,
    canDrag,
    disableDragAndDrop,
    t,
    tInsight,
    locationId,
    locationName,
    onDeleteEvent,
    onEditEvent,
  }) => {
    const history = useHistory();

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: event.id!,
      disabled: !canDrag,
    });

    // Base style for drag transform
    const baseStyle = canDrag
      ? {
          transform: CSS.Transform.toString(transform),
          transition,
          touchAction: "none" as const,
          cursor: isDragging ? "grabbing" : "grab",
        }
      : {};

    // Dragging effect style (similar to DraggableTable)
    const draggingStyle = isDragging
      ? {
          backgroundColor: "#D8E7F0",
          boxShadow: "0 4px 12px rgba(119, 176, 213, 0.3)",
          opacity: 0.9,
          zIndex: 1,
        }
      : {};

    // Handle click on alerts link
    const handleAlertsClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        history.push(
          `/insight-alerts?operationRuleLocationItemIds=${event.id}`
        );
      },
      [history, event.id]
    );

    return (
      <Box
        ref={setNodeRef}
        style={{ ...baseStyle, ...draggingStyle }}
        borderBottom="1px solid"
        borderColor="gray.200"
        py={3}
        px={4}
        bg="white"
        minH="56px"
        display="flex"
        alignItems="center"
        _hover={{ bg: canDrag ? "gray.50" : "white" }}
        {...(canDrag ? { ...attributes, ...listeners } : {})}
      >
        <Flex alignItems="center" justifyContent="space-between" w="100%">
          {/* Left Side - Drag Handle Icon and Event Name */}
          <Flex alignItems="center" gap={3} flex="1" minW="0">
            {/* Drag Handle Icon - Always reserve space to prevent layout shift */}
            <Box
              w="16px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              {canDrag && (
                <Icon
                  as={GripHorizontal}
                  boxSize={4}
                  color="gray.400"
                  opacity={0.5}
                />
              )}
            </Box>

            {/* Event Name */}
            <Text fontSize="14px" color="gray.700" noOfLines={1}>
              {event.name}
            </Text>
          </Flex>

          {/* Center - Alerts Info (hidden when in drag mode) */}
          {event.operationRuleAlerts && event.operationRuleAlerts.length > 0 && (
            <Flex
              alignItems="center"
              justifyContent="center"
              minW="100px"
              flex="1"
              maxW="200px"
              visibility={canDrag ? "hidden" : "visible"}
            >
              <Tooltip
                hasArrow
                placement="top"
                label={event.operationRuleAlerts
                  .map((alert) => alert.name)
                  .join("\n")}
                whiteSpace="pre-line"
              >
                <Flex
                  alignItems="center"
                  gap={1}
                  color="#1E5297"
                  fontSize="14px"
                  cursor="pointer"
                  _hover={{
                    textDecoration: "underline",
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={handleAlertsClick}
                >
                  <Text>
                    {event.operationRuleAlerts.length === 1
                      ? tInsight("oneAlert")
                      : `${event.operationRuleAlerts.length} ${tInsight(
                          "multipleAlerts"
                        )}`}
                  </Text>
                  <Icon as={ChevronRight} boxSize={4} />
                </Flex>
              </Tooltip>
            </Flex>
          )}

          {/* Right Side - Action Icons (hidden when in drag mode) */}
          {!disableDragAndDrop && (
            <Flex
              alignItems="center"
              gap={2}
              visibility={canDrag ? "hidden" : "visible"}
            >
              {/* Edit Icon */}
              <IconButton
                aria-label={t("editEvent")}
                icon={<Icon as={Pencil} boxSize={4} color="gray.500" />}
                variant="ghost"
                size="sm"
                _hover={{ bg: "gray.100" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditEvent?.(event, locationId, locationName);
                }}
              />

              {/* Delete Icon */}
              <IconButton
                aria-label={t("deleteEvent")}
                icon={<Icon as={Trash2} boxSize={4} color="gray.500" />}
                variant="ghost"
                size="sm"
                _hover={{ bg: "red.50", color: "red.500" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteEvent?.(event.id!, event.name, locationId);
                }}
              />
            </Flex>
          )}
        </Flex>
      </Box>
    );
  }
);

const EventList: React.FC<EventListProps> = observer(
  ({
    events,
    locationId,
    locationName,
    isLoading = false,
    error = null,
    disableDragAndDrop = false,
    isDraggable,
    saveKey = 0,
    onDragDataChange,
    onDeleteEvent,
    onEditEvent,
  }) => {
    const { t } = useTranslation("translation", {
      keyPrefix: "oceanWeatherIntelligence.insightManager.eventManager",
    });

    // Translation for insight-related strings (oneAlert, multipleAlerts)
    const { t: tInsight } = useTranslation("translation", {
      keyPrefix: "oceanWeatherIntelligence.operationRuleLocationInsight",
    });

    const [currentData, setCurrentData] =
      useState<ILocationMenuInsight[]>(events);

    // Track previous isDraggable state to detect when exiting drag mode
    const prevIsDraggableRef = useRef(isDraggable);

    // Track previous saveKey to detect when save was confirmed
    const prevSaveKeyRef = useRef(saveKey);

    // Update local data when events prop changes (only when not in drag mode)
    useEffect(() => {
      if (!isDraggable) {
        setCurrentData(events);
      }
    }, [events, isDraggable]);

    // Handle drag mode changes from parent - reset to original events when canceling (NOT when saving)
    useEffect(() => {
      const wasInDragMode = prevIsDraggableRef.current;
      const isNowInDragMode = isDraggable;
      const saveKeyChanged = saveKey !== prevSaveKeyRef.current;

      // Update refs
      prevSaveKeyRef.current = saveKey;
      prevIsDraggableRef.current = isDraggable;

      // Only reset to events when CANCELING (exiting drag mode WITHOUT saveKey change)
      // If saveKey changed, it means save was confirmed - keep current order
      if (wasInDragMode && !isNowInDragMode && !saveKeyChanged) {
        setCurrentData(events);
      }
    }, [isDraggable, events, saveKey]);

    // Notify parent when drag data changes
    useEffect(() => {
      if (isDraggable && onDragDataChange) {
        onDragDataChange(locationId, currentData);
      }
    }, [currentData, isDraggable, locationId, onDragDataChange]);

    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );

    const sortableItems = useMemo(
      () => currentData.map((item) => item.id!),
      [currentData]
    );

    const canDrag = isDraggable && !disableDragAndDrop;

    const handleDragEnd = useCallback(
      ({ active, over }: DragEndEvent) => {
        if (disableDragAndDrop) return;
        if (!over || active.id === over.id) return;

        const oldIndex = currentData.findIndex((i) => i.id === active.id);
        const newIndex = currentData.findIndex((i) => i.id === over.id);
        const newData = arrayMove(currentData, oldIndex, newIndex);

        setCurrentData(newData);
      },
      [currentData, disableDragAndDrop]
    );

    return (
      <Box>
        {/* Events List */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortableItems}
            strategy={verticalListSortingStrategy}
          >
            {currentData.map((event) => (
              <SortableRow
                key={event.id}
                event={event}
                canDrag={canDrag}
                disableDragAndDrop={disableDragAndDrop}
                t={t}
                tInsight={tInsight}
                locationId={locationId}
                locationName={locationName}
                onDeleteEvent={onDeleteEvent}
                onEditEvent={onEditEvent}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Box>
    );
  }
);

export default EventList;
